import type {
  AnalyticsTableColumn,
  AnalyticsTableDataRow,
  AnalyticsTableRow,
} from '../../../../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'
import type {
  AnalyticsTableCellValue,
  BreakdownValue,
} from '../../../../analyticsModule/components/AnalyticsTable/components/AnalyticsTableCell/AnalyticsTableCell.types'
import type { AnalyticsReportData, AnalyticsReportRow } from '../../../../types'
import { extractPrimitiveValue } from '../../../../utils/extractPrimitiveValue'

type Money = {
  value: number
  currency: string
}

type DateRange = {
  from: string
  to: string
}

type Value = string | number | Money | DateRange | ResponseBreakdownValue | null

type ResponseBreakdownValue = {
  value: Value
  elements?: Record<string, Value>
}

const convertValueType = (
  value: Value,
  type: AnalyticsTableColumn['type'],
): AnalyticsTableCellValue => {
  if (type === 'date') {
    if (typeof value === 'object') {
      return {
        from: new Date((value as DateRange).from),
        to: new Date((value as DateRange).to),
      }
    }

    return new Date(value as string)
  }

  return (value ?? '') as AnalyticsTableCellValue
}

const getValue = (value: Value, column: AnalyticsTableColumn): AnalyticsTableCellValue => {
  if (!value) return ''

  if (typeof value === 'object' && 'elements' in value) {
    return {
      value: convertValueType((value as ResponseBreakdownValue).value, column.type),
      breakdown: {
        title: column.breakdown?.label ?? undefined,
        items: Object.entries((value as ResponseBreakdownValue).elements!)
          .map(([key, value]) => {
            const label = column.breakdown!.elements.find(({ id }) => id === key)?.label

            if (!label || !value) return

            return {
              label,
              value: convertValueType(value as Value, column.type),
            }
          })
          .filter(Boolean),
      },
    } as BreakdownValue
  }

  return convertValueType(value, column.type)
}

export const createAnalyticsTableData = (
  data: AnalyticsReportData | undefined,
  columns: AnalyticsTableColumn[],
  sorting?: {
    columnId: string
    direction: 'asc' | 'desc'
  },
) => {
  const tableData: AnalyticsTableDataRow[] = []

  if (!columns.length || !data?.report) {
    return tableData
  }

  const reportData = data.report

  if (reportData.summary) {
    const summaryRow: Record<string, AnalyticsTableCellValue> & AnalyticsTableRow = {}

    Object.entries(reportData.summary).map(([key, value]) => {
      if (value === null) return

      const column = columns.find(({ id }) => id === key)
      if (!column) return

      summaryRow[key] = getValue(value, column)
    })

    // Mark summary row and add unique ID
    summaryRow.rowId = 'summary-row'
    summaryRow.isNested = false
    summaryRow.isExpandable = false

    tableData.push(summaryRow)
  }

  const processRow = (
    row: AnalyticsReportRow,
    parentRowIndex?: number,
    parentRowId?: string,
    isNested: boolean = false,
    nestedLevel: number = 0,
  ) => {
    const tableRow: AnalyticsTableDataRow = {}

    Object.entries(row).forEach(([key, value]) => {
      const column = columns.find(({ id }) => id === key)
      if (!column) return

      tableRow[key] = getValue(value as Value, column)
    })

    // Generate unique row ID based on first column value
    const firstColumn = columns[0]
    const firstColumnValue = firstColumn ? tableRow[firstColumn.id] : null
    const rowId =
      isNested && parentRowId
        ? `${parentRowId}-${String(firstColumnValue)}-${nestedLevel}`
        : `row-${String(firstColumnValue)}-${tableData.length}`

    // Set expandable row properties
    tableRow.rowId = rowId
    tableRow.parentRowId = parentRowId
    tableRow.isExpandable = !!(row.other_rows && row.other_rows.length > 0)
    tableRow.isExpanded = false // Default to collapsed
    tableRow.isNested = isNested
    tableRow.parentRowIndex = parentRowIndex
    tableRow.nestedLevel = nestedLevel
    tableRow.metadata = row.metadata

    tableData.push(tableRow)

    // Store nested rows data for later processing
    if (row.other_rows && row.other_rows.length > 0) {
      const currentRowIndex = tableData.length - 1
      tableRow.nestedRowsData = row.other_rows.map((nestedRow, nestedIndex: number) => {
        // Process nested row data similar to parent row
        const processedNestedRow: AnalyticsTableDataRow = {}

        Object.entries(nestedRow).forEach(([key, value]) => {
          const column = columns.find(({ id }) => id === key)
          if (!column) return

          processedNestedRow[key] = getValue(value as Value, column)
        })

        // Generate unique ID for nested row
        const nestedRowId = `${rowId}-nested-${nestedIndex}`

        // Add nested row properties
        processedNestedRow.rowId = nestedRowId
        processedNestedRow.parentRowId = rowId
        processedNestedRow.isExpandable = false // Nested rows don't have their own nested rows
        processedNestedRow.isExpanded = false
        processedNestedRow.isNested = true
        processedNestedRow.parentRowIndex = currentRowIndex
        processedNestedRow.nestedLevel = nestedLevel + 1

        return processedNestedRow
      })
    }
  }

  reportData.rows.forEach((row) => {
    processRow(row)
  })

  if (sorting) {
    const sortColumn = columns.find((column) => column.id === sorting.columnId)

    if (sortColumn) {
      const compareValues = (a: any, b: any): number => {
        if (sortColumn.type === 'date') {
          const aDate = a instanceof Date ? a : new Date(a)
          const bDate = b instanceof Date ? b : new Date(b)

          if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0
          if (isNaN(aDate.getTime())) return 1
          if (isNaN(bDate.getTime())) return -1

          return aDate.getTime() - bDate.getTime()
        }

        if (sortColumn.type === 'money' || sortColumn.type === 'number') {
          const aPrimitive = extractPrimitiveValue(a)
          const bPrimitive = extractPrimitiveValue(b)

          const aNum = typeof aPrimitive === 'number' ? aPrimitive : 0
          const bNum = typeof bPrimitive === 'number' ? bPrimitive : 0

          return aNum - bNum
        }

        const aStr = String(a || '')
        const bStr = String(b || '')

        return aStr.localeCompare(bStr)
      }

      // Sort while preserving hierarchy
      const sortWithHierarchy = (
        rows: (Record<string, AnalyticsTableCellValue> & AnalyticsTableRow)[],
      ) => {
        const sortedRows: (Record<string, AnalyticsTableCellValue> & AnalyticsTableRow)[] = []
        const processedIndices = new Set<number>()

        // First, sort only parent rows (non-nested)
        const parentRows = rows.filter(
          (row, index) => !row.isNested && !processedIndices.has(index),
        )
        parentRows.sort((a, b) => {
          const aValue = a[sorting.columnId]
          const bValue = b[sorting.columnId]

          const comparison = compareValues(aValue, bValue)

          return sorting.direction === 'asc' ? comparison : -comparison
        })

        // For each parent row, add it and its nested children
        parentRows.forEach((parentRow) => {
          const parentIndex = rows.findIndex((row) => row === parentRow)
          processedIndices.add(parentIndex)
          const newParentIndex = sortedRows.length
          sortedRows.push(parentRow)

          // Sort nested rows from nestedRowsData (not from main rows array)
          // Note: nested rows are stored in nestedRowsData and are NOT in the main rows array
          // They are added to visibleData dynamically when parent is expanded
          if (parentRow.nestedRowsData && parentRow.nestedRowsData.length > 0) {
            parentRow.nestedRowsData.sort((a, b) => {
              const aValue = a[sorting.columnId]
              const bValue = b[sorting.columnId]

              const comparison = compareValues(aValue, bValue)

              return sorting.direction === 'asc' ? comparison : -comparison
            })

            // Update nestedRowsData parentRowIndex after sorting
            parentRow.nestedRowsData.forEach((nestedRow) => {
              nestedRow.parentRowIndex = newParentIndex
            })
          }
        })

        return sortedRows
      }

      // Apply sorting with hierarchy preservation
      const summaryRow = reportData.summary ? tableData[0] : null
      const dataRows = reportData.summary ? tableData.slice(1) : tableData

      const sortedDataRows = sortWithHierarchy(dataRows)

      // Reconstruct table data with summary row at the top if it exists
      tableData.length = 0
      if (summaryRow) {
        tableData.push(summaryRow)
      }
      tableData.push(...sortedDataRows)
    }
  }

  return tableData
}
